import type { QueryClient } from "@tanstack/react-query";
import { getBuyerGuide } from "@/data/buyerGuides";
import { getHardwareCategory, getHardwareProduct, getSubAssemblyGuide, isFeaturedHardwareProduct } from "@/data/hardwareCatalog";
import { assets, materials, realPhotos, siteConfig } from "@/data/site";
import { getTechnicalResource } from "@/data/technicalResources";
import { accessoryCategoryDetails, mountingStyles, privacyOptions } from "@/data/resources";
import { getCityServiceArea } from "@/data/cityServiceAreas";
import { stateAreaPages } from "@/data/stateAreaPages";

export type HeadMeta = {
  title: string;
  description: string;
  ogType?: "website" | "article";
  ogImage?: string;
  ogImageWidth?: number;
  ogImageHeight?: number;
  ogImageAlt?: string;
  canonicalPath?: string;
  noindex?: boolean;
  notFound?: boolean;
};

const defaultHead = {
  title: "Commercial Toilet Partitions & Bathroom Stalls | Partition Stalls",
  description:
    "Commercial toilet partitions, bathroom stalls, and restroom accessories supplied nationwide. Compare five materials, plan a budget, and request a quote.",
  ogType: "website" as const,
  ogImage: assets.socialCard,
  ogImageWidth: 1200,
  ogImageHeight: 630,
  ogImageAlt: "Partition Stalls branded homepage preview for commercial bathroom partitions and restroom accessories",
};

function withDefaults(head: Partial<HeadMeta> & Pick<HeadMeta, "title" | "description">): HeadMeta {
  return { ...defaultHead, ...head };
}

function cleanPath(url: string) {
  let path = url.split("?")[0] || "/";
  try {
    path = decodeURI(path);
  } catch {
    // Match wouter's fail-safe behavior for malformed paths.
  }
  return path.replace(/\/+$/, "") || "/";
}

function appendBoundedMetadataSuffix(value: string, suffix: string) {
  const description = `${value.trim()}${suffix}`;
  if (description.length <= 155) return description;
  const available = 155 - suffix.length - 1;
  const truncated = value.slice(0, available).replace(/\s+\S*$/, "").trimEnd();
  return `${truncated}…${suffix}`;
}

function appendBoundedMetadataTitle(value: string, suffix = " | Partition Stalls") {
  const title = `${value.trim()}${suffix}`;
  if (title.length <= 65) return title;
  const available = 65 - suffix.length - 1;
  const truncated = value.slice(0, available).replace(/\s+\S*$/, "").trimEnd();
  return `${truncated}…${suffix}`;
}

export function getCoreHeadForPath(url: string): HeadMeta {
  const path = cleanPath(url);

  if (path === "/") return withDefaults({ ...defaultHead, canonicalPath: "/" });
  if (path === "/bathroom-partitions") {
    return withDefaults({
      title: "Commercial Bathroom & Toilet Partitions | Partition Stalls",
      description:
        "Compare five toilet partition materials for bathroom stalls. Review mounting, privacy, replacement hardware, accessibility planning, and budgets.",
      canonicalPath: path,
    });
  }
  if (path === "/partition-budget-calculator") {
    return withDefaults({
      title: "Toilet Partition Costs & Budget Calculator | Partition Stalls",
      description:
        "Estimate bathroom stall costs by material and quantity. Add privacy and installation planning allowances, download your budget guide, then request a quote.",
      canonicalPath: path,
    });
  }

  const materialMatch = path.match(/^\/bathroom-partitions\/([^/]+)$/i);
  if (materialMatch) {
    const material = materials.find((item) => item.slug.toLowerCase() === materialMatch[1].toLowerCase());
    if (!material) {
      return withDefaults({
        title: `Page Not Found | ${siteConfig.name}`,
        description: "The requested commercial bathroom partition page could not be found.",
        notFound: true,
      });
    }
    return withDefaults({
      title: `${material.name} Bathroom Partitions | Partition Stalls`,
      description: material.metaDescription,
      canonicalPath: `/bathroom-partitions/${material.slug}`,
    });
  }

  if (path === "/commercial-restroom-accessories") {
    return withDefaults({
      title: "Commercial Restroom Accessories & Brands | Partition Stalls",
      description:
        "Browse 919 supplied ASI, Bobrick, and Bradley commercial restroom accessory records. Filter by category or verified finish and request project pricing.",
      canonicalPath: path,
    });
  }

  if (path === "/accessory-quote-cart") {
    return withDefaults({
      title: "Accessory Quote List | Partition Stalls",
      description:
        "Review saved commercial restroom accessory models, print a PDF copy, or send the selection through a secure project quote request.",
      canonicalPath: path,
      noindex: true,
    });
  }

  const accessoryMatch = path.match(/^\/commercial-restroom-accessories\/([^/]+)$/i);
  if (accessoryMatch) {
    const category = accessoryCategoryDetails.find((item) => item.id.toLowerCase() === accessoryMatch[1].toLowerCase());
    if (!category) {
      return withDefaults({
        title: `Page Not Found | ${siteConfig.name}`,
        description: "The requested commercial restroom accessory category could not be found.",
        notFound: true,
      });
    }
    return withDefaults({
      title: `${category.eyebrow} | Partition Stalls`,
      description: category.metaDescription,
      canonicalPath: `/commercial-restroom-accessories/${category.id}`,
    });
  }

  const hardwareProductMatch = path.match(/^\/hardware\/([^/]+)\/([^/]+)$/i);
  if (hardwareProductMatch) {
    const product = getHardwareProduct(hardwareProductMatch[1], hardwareProductMatch[2]);
    if (!product) {
      return withDefaults({
        title: `Page Not Found | ${siteConfig.name}`,
        description: "The requested hardware reference could not be found.",
        notFound: true,
      });
    }
    return withDefaults({
      title: `${product.brand} ${product.model} Reference | Partition Stalls`,
      description: appendBoundedMetadataSuffix(product.description, " Reference-price information supports early quote preparation."),
      canonicalPath: product.path,
      noindex: !isFeaturedHardwareProduct(product),
    });
  }
  const hardwareCategoryMatch = path.match(/^\/hardware\/([^/]+)$/i);
  if (hardwareCategoryMatch) {
    const category = getHardwareCategory(hardwareCategoryMatch[1]);
    if (!category) {
      return withDefaults({
        title: `Page Not Found | ${siteConfig.name}`,
        description: "The requested hardware category could not be found.",
        notFound: true,
      });
    }
    return withDefaults({
      title: `${category.name} | Partition Stalls`,
      description: category.summary,
      canonicalPath: `/hardware/${category.slug}`,
    });
  }
  if (path === "/sub-assemblies") {
    return withDefaults({
      title: "Restroom Hardware Sub-assembly Guides | Partition Stalls",
      description: "Compare documented commercial restroom accessory sub-assemblies and prepare a more focused project quote request.",
      canonicalPath: path,
    });
  }
  const subAssemblyMatch = path.match(/^\/sub-assemblies\/([^/]+)$/i);
  if (subAssemblyMatch) {
    const guide = getSubAssemblyGuide(subAssemblyMatch[1]);
    if (!guide) {
      return withDefaults({
        title: `Page Not Found | ${siteConfig.name}`,
        description: "The requested sub-assembly guide could not be found.",
        notFound: true,
      });
    }
    return withDefaults({
      title: appendBoundedMetadataTitle(`${guide.name} Guide`),
      description: appendBoundedMetadataSuffix(guide.summary, ""),
      canonicalPath: `/sub-assemblies/${guide.slug}`,
      ogType: "article",
    });
  }
  if (path === "/technical-resources") {
    return withDefaults({
      title: "Technical Resource Library | Partition Stalls",
      description: "Commercial restroom planning guides, checklists, and technical quote-preparation resources.",
      canonicalPath: path,
      ogType: "article",
    });
  }
  const technicalResourceMatch = path.match(/^\/technical-resources\/([^/]+)$/i);
  if (technicalResourceMatch) {
    const resource = getTechnicalResource(technicalResourceMatch[1]);
    if (!resource) {
      return withDefaults({
        title: `Page Not Found | ${siteConfig.name}`,
        description: "The requested technical resource could not be found.",
        notFound: true,
      });
    }
    return withDefaults({
      title: appendBoundedMetadataTitle(resource.metaTitle.replace(/\s*\|\s*Partition Stalls$/i, "")),
      description: appendBoundedMetadataSuffix(resource.metaDescription, ""),
      canonicalPath: `/technical-resources/${resource.slug}`,
      ogType: "article",
    });
  }

  if (path === "/resources/buyer-guides") {
    return withDefaults({
      title: "Commercial Bathroom Partition Buyer Guides | Partition Stalls",
      description: "Practical commercial bathroom partition Buyer Guides for material selection, budget planning, quote preparation, and supplier evaluation.",
      canonicalPath: path,
      ogType: "article",
      ogImage: assets.planningDrawing,
      ogImageWidth: 1600,
      ogImageHeight: 873,
      ogImageAlt: "Commercial restroom plan with partitions, fixtures, and accessories",
    });
  }
  const buyerGuideMatch = path.match(/^\/resources\/buyer-guides\/([^/]+)$/i);
  if (buyerGuideMatch) {
    const guide = getBuyerGuide(buyerGuideMatch[1].toLowerCase());
    if (!guide) {
      return withDefaults({
        title: `Page Not Found | ${siteConfig.name}`,
        description: "The requested commercial bathroom partition buyer guide could not be found.",
        notFound: true,
      });
    }
    return withDefaults({
      title: guide.metaTitle,
      description: guide.metaDescription,
      canonicalPath: `/resources/buyer-guides/${guide.slug}`,
      ogType: "article",
    });
  }
  if (path === "/resources") {
    return withDefaults({
      title: "Commercial Restroom Planning Resources | Partition Stalls",
      description: "Plan commercial bathroom partitions and accessories with material, mounting, measuring, finish, accessibility, FAQ, and quote-preparation guidance.",
      canonicalPath: path,
    });
  }
  if (path === "/resources/mounting-styles") {
    return withDefaults({
      title: "Bathroom Partition Mounting Styles Guide | Partition Stalls",
      description: "Compare overhead-braced, floor-anchored, ceiling-hung, and floor-to-ceiling commercial bathroom partition mounting systems and planning conditions.",
      canonicalPath: path,
      ogImage: mountingStyles[0].image.src,
      ogImageWidth: mountingStyles[0].image.width,
      ogImageHeight: mountingStyles[0].image.height,
      ogImageAlt: mountingStyles[0].image.alt,
    });
  }
  if (path === "/resources/privacy-options") {
    return withDefaults({
      title: "Bathroom Partition Privacy Options & Heights | Partition Stalls",
      description: "Compare four powder-coated, headrail-braced bathroom partition privacy options: 58, 64, 72, and 84 or 92 inch coverage with floor-clearance guidance.",
      canonicalPath: path,
      ogImage: privacyOptions[0].image.src,
      ogImageWidth: privacyOptions[0].image.width,
      ogImageHeight: privacyOptions[0].image.height,
      ogImageAlt: privacyOptions[0].image.alt,
    });
  }
  if (path === "/resources/how-to-measure") {
    return withDefaults({
      title: "How to Measure for Bathroom Partitions | Partition Stalls",
      description: "Record toilet partition dimensions, fixture centerlines, door swings, and accessible or ambulatory stall planning inputs before a project-specific quote.",
      canonicalPath: path,
    });
  }
  if (path === "/resources/colors-finishes") {
    return withDefaults({
      title: "Bathroom Partition Colors & Finishes | Partition Stalls",
      description: "Compare colors, textures, metal finishes, hardware, and accessories across five commercial bathroom partition material families.",
      canonicalPath: path,
    });
  }
  if (path === "/resources/faqs") {
    return withDefaults({
      title: "Commercial Bathroom Partition FAQs | Partition Stalls",
      description: "Get answers about partition materials, wet areas, mounting, measuring, replacement parts, accessories, delivery, and quote preparation.",
      canonicalPath: path,
    });
  }
  if (path === "/contact-partition-stalls") {
    return withDefaults({
      title: "Contact Partition Stalls | Schedule a Callback",
      description: "Reserve a 30-minute callback today or within the next 10 business days for partition, accessory, replacement, planning, or quote help.",
      canonicalPath: path,
    });
  }
  if (path === "/service-areas") {
    return withDefaults({
      title: "Nationwide Bathroom Partition Shipping | Partition Stalls",
      description: "Partition materials and accessories shipped nationwide. Supply and installation in NY, NJ, PA, CT, FL, TX and CA reviewed per project.",
      canonicalPath: path,
      ogImage: assets.nationwideShippingArtworkLarge,
      ogImageWidth: 1800,
      ogImageHeight: 1440,
      ogImageAlt: "Partition Stalls delivery van and miniature bathroom partition stalls connected across a continental U.S. nationwide shipping map",
    });
  }
  if (path === "/service-areas/new-york-city") {
    return withDefaults({
      title: "NYC & Long Island Bathroom Partitions | Partition Stalls",
      description: "Toilet partitions and bathroom stalls for Manhattan, Brooklyn, Queens, the Bronx, Staten Island, and Long Island. Request supply and installation review.",
      canonicalPath: path,
      ogImage: realPhotos.projects.pierSixtyStalls.large,
      ogImageWidth: 1536,
      ogImageHeight: 2048,
      ogImageAlt: "Stainless-steel commercial restroom stalls in the men’s locker room at Pier Sixty in Chelsea Piers, New York City",
    });
  }
  const cityAreaMatch = path.match(/^\/service-areas\/([^/]+)\/([^/]+)$/i);
  if (cityAreaMatch) {
    const cityArea = getCityServiceArea(cityAreaMatch[1].toLowerCase(), cityAreaMatch[2].toLowerCase());
    if (!cityArea) {
      return withDefaults({
        title: `Page Not Found | ${siteConfig.name}`,
        description: "The requested commercial bathroom partition service-area page could not be found.",
        notFound: true,
      });
    }
    return withDefaults({
      title: cityArea.metaTitle,
      description: cityArea.metaDescription,
      canonicalPath: `/service-areas/${cityArea.stateSlug}/${cityArea.citySlug}`,
      ogImage: assets.serviceAreasArtworkLarge,
      ogImageWidth: 1800,
      ogImageHeight: 1440,
      ogImageAlt: `Partition Stalls commercial bathroom partition supply and project-specific installation review for ${cityArea.city}, ${cityArea.stateName}`,
    });
  }
  const stateAreaMatch = path.match(/^\/service-areas\/([^/]+)$/i);
  if (stateAreaMatch) {
    const statePage = stateAreaPages.find((item) => item.slug.toLowerCase() === stateAreaMatch[1].toLowerCase());
    if (!statePage) {
      return withDefaults({
        title: `Page Not Found | ${siteConfig.name}`,
        description: "The requested commercial bathroom partition service-area page could not be found.",
        notFound: true,
      });
    }
    const primaryProject = statePage.featuredProjects?.[0];
    return withDefaults({
      title: statePage.metaTitle,
      description: statePage.metaDescription,
      canonicalPath: `/service-areas/${statePage.slug}`,
      ogImage: primaryProject ? realPhotos.projects[primaryProject.imageKey].large : assets.serviceAreasArtworkLarge,
      ogImageWidth: primaryProject?.imageWidth ?? 1800,
      ogImageHeight: primaryProject?.imageHeight ?? 1440,
      ogImageAlt: primaryProject?.imageAlt ?? `Partition Stalls full-service commercial bathroom partition coverage for ${statePage.slug.replaceAll("-", " ")}`,
    });
  }
  if (path === "/why-partition-stalls") {
    return withDefaults({
      title: "Why Choose Partition Stalls | Nationwide Restroom Supplier",
      description:
        "Choose Partition Stalls for five partition materials, restroom accessories, coordinated quotes, nationwide supply, and project support.",
      canonicalPath: path,
    });
  }
  if (path === "/projects") {
    return withDefaults({
      title: "Commercial Restroom Project Gallery | Partition Stalls",
      description:
        "Browse documented commercial bathroom partition projects by state, material, and facility type. Review verified scope, photos, and project context.",
      canonicalPath: path,
      ogImage: realPhotos.projects.pierSixtyStalls.large,
      ogImageWidth: 1536,
      ogImageHeight: 2048,
      ogImageAlt: "Stainless-steel commercial restroom stalls in the men’s locker room at Pier Sixty in Chelsea Piers, New York City",
    });
  }
  if (path === "/request-a-quote") {
    return withDefaults({
      title: "Request a Commercial Bathroom Partition Quote | Partition Stalls",
      description:
        "Request bathroom partition, toilet stall, and restroom accessory pricing. Share material, quantity, plans, and project details.",
      canonicalPath: path,
    });
  }
  if (path === "/privacy-policy") {
    return withDefaults({
      title: "Privacy Policy | Partition Stalls",
      description: "Review how Partition Stalls handles quote, callback, website, file, analytics, and Google Maps review information.",
      canonicalPath: path,
    });
  }
  if (path === "/terms-of-use") {
    return withDefaults({
      title: "Terms of Use | Partition Stalls",
      description: "Review the terms governing use of the Partition Stalls website, quote requests, project files, and third-party content.",
      canonicalPath: path,
    });
  }

  return withDefaults({
    title: `Page Not Found | ${siteConfig.name}`,
    description: "The requested Partition Stalls page could not be found.",
    notFound: true,
  });
}
